import { Box, Button, IconButton, TextField, Typography } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";

import { supportApi } from "../../client.js";
import type { AdminTicketWatcher } from "../../types.js";

/**
 * Compact watcher list + add control. Staff agents use this to loop
 * teammates into a ticket so they get the activity-feed emails — same
 * fan-out as Rails `Notifications::DeliverTicketActivity` minus the
 * actor and the requester.
 *
 * Wire-up:
 *   POST   /api/admin/tickets/:id/watchers          { user_id }
 *   DELETE /api/admin/tickets/:id/watchers/:user_id
 *
 * Both calls are idempotent on the Rails side (see
 * `Tickets::AddWatcher` / `Tickets::RemoveWatcher`), so we don't need
 * to optimistic-update here — we just `onChange()` and let the parent
 * refetch the canonical list from the admin show endpoint.
 *
 * The add-input is a freeform user id for now (the design memo for
 * Task 14 calls out that a typeahead-against-`listAssignableUsers` is
 * a follow-up — this is the smallest working seam).
 */
export function WatcherMenu({
  ticketId,
  watchers,
  onChange,
}: {
  ticketId: number | string;
  watchers: AdminTicketWatcher[];
  onChange: () => void;
}): JSX.Element {
  const [userId, setUserId] = useState("");

  const addWatcher = useMutation({
    mutationFn: (uid: number) =>
      supportApi.addAdminTicketWatcher(ticketId, { user_id: uid }),
    onSuccess: () => {
      setUserId("");
      onChange();
    },
  });

  const removeWatcher = useMutation({
    mutationFn: (uid: number) =>
      supportApi.removeAdminTicketWatcher(ticketId, uid),
    onSuccess: () => onChange(),
  });

  function submitAdd(): void {
    const parsed = Number(userId);
    if (!Number.isFinite(parsed) || parsed <= 0) return;
    addWatcher.mutate(parsed);
  }

  const busy = addWatcher.isPending || removeWatcher.isPending;

  return (
    <Box
      component="section"
      aria-label="Watchers"
      sx={{
        display: "grid",
        gap: 1,
        borderRadius: 1,
        border: 1,
        borderColor: "divider",
        bgcolor: "background.paper",
        boxShadow: (t) => t.shadows[1],
        p: 1.5,
      }}
    >
      <Box component="header" sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Typography component="h3" sx={{ fontSize: 14, fontWeight: 600, letterSpacing: "-0.01em", color: "text.primary" }}>
          Watchers
        </Typography>
        {watchers.length > 0 && (
          <Box
            component="span"
            sx={{
              borderRadius: 999,
              bgcolor: "action.hover",
              px: 1,
              py: 0.25,
              fontSize: 10,
              fontWeight: 500,
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              color: "text.disabled",
            }}
          >
            {watchers.length}
          </Box>
        )}
      </Box>

      {watchers.length === 0 ? (
        <Typography sx={{ fontSize: 12, color: "text.disabled" }}>
          No teammates watching yet.
        </Typography>
      ) : (
        <Box component="ul" sx={{ display: "grid", gap: 0.5, listStyle: "none", m: 0, p: 0 }}>
          {watchers.map((w) => (
            <Box
              component="li"
              key={w.user_id}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                borderRadius: 1,
                border: 1,
                borderColor: "divider",
                bgcolor: "background.default",
                px: 1,
                py: 0.5,
                fontSize: 14,
              }}
            >
              <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: "text.primary" }}>
                {w.user_name}
              </Box>
              <IconButton
                size="small"
                aria-label={`Remove ${w.user_name}`}
                onClick={() => removeWatcher.mutate(w.user_id)}
                disabled={busy}
                sx={{ color: "text.disabled", "&:hover": { color: "text.primary" } }}
              >
                ×
              </IconButton>
            </Box>
          ))}
        </Box>
      )}

      <Box
        component="form"
        onSubmit={(e) => {
          e.preventDefault();
          submitAdd();
        }}
        sx={{ display: "grid", gridTemplateColumns: "1fr auto", alignItems: "flex-end", gap: 1 }}
      >
        <TextField
          label="Add watcher (user id)"
          value={userId}
          onChange={(e) => setUserId(e.target.value)}
          disabled={busy}
          inputProps={{ inputMode: "numeric" }}
          placeholder="e.g. 42"
          fullWidth
          InputLabelProps={{ shrink: true }}
        />
        <Button
          type="submit"
          variant="contained"
          disabled={busy || userId.trim() === ""}
        >
          Add
        </Button>
      </Box>
      {(addWatcher.isError || removeWatcher.isError) && (
        <Typography role="alert" sx={{ fontSize: 12, color: "error.main" }}>
          That didn’t go through. Try again.
        </Typography>
      )}
    </Box>
  );
}
